/**
 * receiptService.js
 *
 * Client for the Order Receipt API.
 *
 * Phase 2 — swaps Receipt.jsx from mock data to live backend.
 * Backend spec: docs/backend/RECEIPT_BACKEND.md
 * API contract:  API_CONTRACT.md  §4 (GET /api/orders/:id/receipt)
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Fetch the receipt JSON for a paid order.
 *
 * @param {string} orderId  - The order ID from the URL param
 * @param {string} idToken  - Firebase ID token from getIdToken()
 * @returns {Promise<object>} receipt object matching receiptMock shape
 *
 * Error handling per AGENTS.md §6:
 *   503 → throws ReceiptDisabledError  (caller hides feature silently)
 *   429 → throws ReceiptRateLimitError  (caller shows "please wait" message)
 *   401 → throws ReceiptAuthError       (caller triggers re-login)
 *   403 → throws ReceiptForbiddenError  (caller shows forbidden message)
 *   404 → throws ReceiptNotFoundError   (caller shows not-found message)
 *   400 → throws Error with server message
 *   500 → throws Error with generic message
 */
export const getReceipt = async (orderId, idToken) => {
  const res = await fetch(`${API_URL}/api/orders/${orderId}/receipt`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (res.ok) return res.json();

  // Parse error body once
  const err = await res.json().catch(() => ({}));
  const message = err.error ?? err.message ?? `Request failed: ${res.status}`;

  if (res.status === 503) throw new ReceiptDisabledError(message);
  if (res.status === 429) throw new ReceiptRateLimitError(message);
  if (res.status === 401) throw new ReceiptAuthError(message);
  if (res.status === 403) throw new ReceiptForbiddenError(message);
  if (res.status === 404) throw new ReceiptNotFoundError(message);

  throw new Error(message);
};

// ── Named error classes for clean handling in Receipt.jsx ────────────────────

export class ReceiptDisabledError extends Error {
  constructor(msg) { super(msg); this.name = 'ReceiptDisabledError'; }
}

export class ReceiptRateLimitError extends Error {
  constructor(msg) { super(msg); this.name = 'ReceiptRateLimitError'; }
}

export class ReceiptAuthError extends Error {
  constructor(msg) { super(msg); this.name = 'ReceiptAuthError'; }
}

export class ReceiptForbiddenError extends Error {
  constructor(msg) { super(msg); this.name = 'ReceiptForbiddenError'; }
}

export class ReceiptNotFoundError extends Error {
  constructor(msg) { super(msg); this.name = 'ReceiptNotFoundError'; }
}
